/*
	the attachment format/size limits. three places have to agree on them -- the server allowlist
	in backend/feathers-server/src/attachment-service.ts, the client predicate in
	frontend/src/pages/chat/message-input-2.tsx, and getFileExtension's type map in
	attachment-service.ts -- and they all read from here, so they cannot disagree. when they held
	separate copies, a file the client accepted could still be rejected by the server (or vice
	versa), with the client and server error text quoting different limits.
*/
/*
	15 MB is a decision, not a measurement. It clears a 50MP phone JPEG with room, and the bytes
	never traverse our server -- the upload is a presigned S3 POST, so API Gateway's and Lambda's
	payload caps are not in this path and S3's own content-length-range is what enforces it.

	It is generous on purpose, and safe to be generous because ThumbnailGenerator downscales the
	stored original to maxStoredImageEdge. Measured on synthetic photo-like sources at q85: a 12MP
	source is 1.6 MB and a 50MP source 5.9 MB, but after the downscale both store at ~410 KB. So
	this number bounds what a user may send, not what we keep, and raising it does not raise the
	S3 bill.
*/
export const maxAttachmentBytes = 15 * 1024 * 1024

/*
	longest edge, in pixels, that a stored original is kept at. Above any phone screen and most
	laptop screens, which matters because message.tsx serves the ORIGINAL to the lightbox -- this
	is a visible quality decision, not an invisible storage optimisation.
*/
export const maxStoredImageEdge = 2048

export const allowedAttachmentContentTypes: string[] = [
	"image/jpeg",
	"image/jpg",
	"image/png",
	"image/gif",
	"image/webp",
]

export const attachmentExtensionByContentType: Record<string, string> = {
	"image/jpeg": "jpg",
	"image/jpg": "jpg",
	"image/png": "png",
	"image/gif": "gif",
	"image/webp": "webp",
}

//	HEIC/HEIF is the iPhone camera default; browsers disagree on which of the two strings they
//	report for the same file (Chromium 146 reports "image/heif" for a real .heic), so both must be
//	recognised. it is recognised in order to be REFUSED with an accurate message -- support is
//	thread ha-613 and has not landed.
export const heicContentTypes: string[] = ["image/heic", "image/heif"]

//	derived from maxAttachmentBytes so the number quoted in the text can never disagree with the
//	number actually enforced.
export function maxAttachmentSizeText(): string {
	return `${Math.round(maxAttachmentBytes / (1024 * 1024))} MB`
}

//	returns a user-facing sentence explaining why `file` must be refused, or undefined when it's
//	acceptable. HEIC/HEIF is checked before the generic format check so an iPhone user gets the
//	specific explanation (and a way to fix it) rather than a generic "only these formats" list.
export function attachmentRejectionReason(file: { type: string; size: number }): string | undefined {
	if (heicContentTypes.includes(file.type))
		return "HEIC/HEIF photos (the iPhone default) aren't supported yet. Change your iPhone camera setting to \"Most Compatible\", or convert the photo to JPEG."
	if (!allowedAttachmentContentTypes.includes(file.type))
		return "Only JPEG, PNG, GIF, or WebP images are accepted."
	if (file.size > maxAttachmentBytes)
		return `This file is too large. Attachments can't be larger than ${maxAttachmentSizeText()}.`
	return undefined
}
